import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { FolderIcon, FileIcon, ContainerIcon } from '../ui/Icons';
import strokeScoringService from '../../services/strokeScoring';
import storageGatewayService from '../../services/storageGateway';
import { createSasBackend, createGatewayBackend } from '../../services/storageBackends';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const PopupContainer = styled.div`
  background-color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  box-shadow: ${theme.shadows.lg};
  font-family: ${theme.typography.fontFamily};
  width: 90%;
  max-width: 1200px;
  height: ${props => props.isConnected ? '500px' : 'auto'};
  max-height: 80vh;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: ${theme.spacing.sm};
`;

const Title = styled.h2`
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.xl};
  font-weight: ${theme.typography.fontWeights.bold};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.error};
  font-size: 24px;
  cursor: pointer;
  margin-left: auto;
  padding: 2px;
  border-radius: ${theme.borderRadius.sm};
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  
  &:hover {
    background-color: ${theme.colors.surfaceHover};
  }
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const Label = styled.label`
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: ${theme.typography.fontWeights.medium};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
`;

const BrowserContainer = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const PathBreadcrumb = styled.div`
  background-color: ${theme.colors.background};
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing.md};
  font-family: monospace;
  font-size: ${theme.typography.fontSizes.sm};
  color: ${theme.colors.textSecondary};
  border: 1px solid ${theme.colors.primary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.text};
  font-size: 16px;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: ${theme.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  
  &:hover {
    background-color: ${theme.colors.surfaceHover};
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const PathText = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileList = styled.div`
  flex: 1;
  overflow-y: auto;
  background-color: ${theme.colors.background};
`;

const FileTable = styled.div`
  display: flex;
  flex-direction: column;
`;

const FileItem = styled.div`
  display: grid;
  grid-template-columns: 1fr 140px 70px 60px 30px;
  gap: ${theme.spacing.sm};
  align-items: center;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  cursor: pointer;
  position: relative;
  background-color: ${theme.colors.background};
  
  &:hover {
    background-color: ${theme.colors.surfaceHover};
  }
`;

const FileNameCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  min-width: 0;
  overflow: hidden;
`;

const FileDataCell = styled.div`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSizes.xs};
  font-family: monospace;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileActions = styled.div`
  margin-left: auto;
  position: relative;
`;

const DropdownButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.textSecondary};
  font-size: 16px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: ${theme.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${theme.colors.surfaceHover};
    color: ${theme.colors.text};
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: ${theme.colors.surface};
  border: 1px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.shadows.lg};
  z-index: 1000;
  min-width: 120px;
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: none;
  border: none;
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.sm};
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  
  &:hover {
    background-color: ${theme.colors.surfaceHover};
  }
  
  &:first-child {
    border-radius: ${theme.borderRadius.md} ${theme.borderRadius.md} 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 ${theme.borderRadius.md} ${theme.borderRadius.md};
  }
  
  &.delete {
    color: ${theme.colors.error};
  }
`;

const FileName = styled.span`
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.sm};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

// Option 1: Solid badges - both green
const LoadedIndicator = styled.span`
  background-color: #50fa7b;
  color: ${theme.colors.background};
  font-size: ${theme.typography.fontSizes.xs};
  font-weight: ${theme.typography.fontWeights.bold};
  padding: 2px 6px;
  border-radius: ${theme.borderRadius.sm};
  margin-left: ${theme.spacing.xs};
  text-transform: uppercase;
`;



const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 140px 70px 60px 30px;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background-color: ${theme.colors.background};
  font-weight: ${theme.typography.fontWeights.medium};
  font-size: ${theme.typography.fontSizes.sm};
`;

const ColumnHeader = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: ${theme.typography.fontWeights.medium};
  cursor: pointer;
  padding: 4px 0;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    color: ${theme.colors.primaryHover};
  }
  
  &:disabled {
    cursor: default;
    color: ${theme.colors.text};
  }
`;

const SortArrow = styled.span`
  font-size: 12px;
  color: ${theme.colors.primary};
`;

const SearchContainer = styled.div`
  position: relative;
  width: 200px;
`;

const SearchInput = styled.input`
  width: 100%;
  background-color: ${theme.colors.surface};
  border: 1px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.sm} 32px;
  outline: none;
  font-family: ${theme.typography.fontFamily};
  
  &:focus {
    border-color: ${theme.colors.primaryHover};
  }
  
  &::placeholder {
    color: ${theme.colors.textSecondary};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textSecondary};
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ErrorMessage = styled.div`
  background-color: ${props => props.isSuccess ? '#50fa7b' : theme.colors.error};
  color: ${props => props.isSuccess ? theme.colors.background : 'white'};
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing.md};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: ${theme.typography.fontWeights.medium};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xl};
  gap: ${theme.spacing.md};
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid ${theme.colors.primary}40;
  border-top: 3px solid ${theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: ${theme.typography.fontWeights.medium};
`;

const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background-color: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.base};
  font-family: ${theme.typography.fontFamily};
  outline: none;
  box-sizing: border-box;
  cursor: pointer;
  transition: ${theme.transitions.normal};

  &:focus {
    border-color: ${theme.colors.primaryHover};
    box-shadow: 0 0 0 2px ${theme.colors.primary}20;
  }
`;

// Shown to employees until the gateway reports what it will actually allow
const FALLBACK_CONTAINERS = [{ name: 'tater-web' }, { name: 'client-videos' }];

const AzureStoragePopup = ({ isOpen, onClose, onLoadAnnotation, onGetCurrentAnnotations, onGetCurrentStrokes, strokesFileName, onLoadVideo, isEmployee = false }) => {
  const [storageAccount, setStorageAccount] = useState('');
  const [sasToken, setSasToken] = useState('');
  const [currentContainer, setCurrentContainer] = useState('tater-web');
  const [containerOptions, setContainerOptions] = useState(FALLBACK_CONTAINERS);
  const [currentPath, setCurrentPath] = useState('');
  const [items, setItems] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Status banner. setError keeps its old signature so the existing call sites
  // are unaffected; only re-scoring reports success, and it says so explicitly
  // rather than the banner inferring it from the message text.
  const [status, setStatus] = useState(null);
  const setError = (message) => setStatus(message ? { message, isSuccess: false } : null);
  const showSuccess = (message) => setStatus({ message, isSuccess: true });
  const [browsingContainers, setBrowsingContainers] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [loadedVideoFile, setLoadedVideoFile] = useState(null);
  const [loadedAnnotationFile, setLoadedAnnotationFile] = useState(null);
  const [sortBy, setSortBy] = useState('name'); // 'name', 'size', 'lastModified'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
  const [searchTerm, setSearchTerm] = useState('');
  // No longer needed - using solid badges directly

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatLastModified = (date) => {
    if (!date) return '';
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleColumnSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const sortItems = (items) => {
    return [...items].sort((a, b) => {
      let comparison = 0;
      
      // Always put folders first
      if (a.type === 'folder' && b.type !== 'folder') return -1;
      if (a.type !== 'folder' && b.type === 'folder') return 1;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'size':
          comparison = a.size - b.size;
          break;
        case 'lastModified':
          if (!a.lastModified && !b.lastModified) comparison = 0;
          else if (!a.lastModified) comparison = 1;
          else if (!b.lastModified) comparison = -1;
          else comparison = a.lastModified.getTime() - b.lastModified.getTime();
          break;
        default:
          comparison = a.name.localeCompare(b.name);
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  };

  const getSortArrow = (column) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const filterItems = (items) => {
    let filtered = items;
    
    // Filter out dummy.txt files
    filtered = filtered.filter(item => 
      item.name.toLowerCase() !== 'dummy.txt'
    );
    
    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  };

  const processItems = (items) => {
    const filtered = filterItems(items);
    return sortItems(filtered);
  };

  // Employees go through the storage gateway, which holds the credentials
  // server-side; everyone else talks to Azure directly with their own SAS token.
  // Both expose the same interface, so nothing below this point cares which.
  const backendFor = (containerName) => (
    isEmployee
      ? createGatewayBackend({ container: containerName })
      : createSasBackend({ storageAccount, sasToken, container: containerName })
  );

  const backend = backendFor(currentContainer);

  const listDirectory = (containerName, path = '') => backendFor(containerName).list(path);

  const refreshCurrentDirectory = async () => {
    try {
      setItems(await listDirectory(currentContainer, currentPath));
    } catch {
      // Best-effort refresh: the operation that triggered it already succeeded
    }
  };

  const connectToStorage = async () => {
    // Employees pick a container and the gateway supplies the credentials
    if (!isEmployee && (!storageAccount || !sasToken || !currentContainer)) {
      setError('Please fill in storage account name, SAS token, and container name');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Connect to the specified container
      const blobs = await listDirectory(currentContainer, '');

      setItems(blobs);
      setIsConnected(true);
      setBrowsingContainers(false); // We're browsing the container, not containers
      setCurrentPath('');
    } catch (err) {
      setError(`Connection failed: ${err.message}`);
      setIsConnected(false);
    } finally {
      setIsLoading(false);
    }
  };

  const navigateToContainer = async (containerName) => {
    setCurrentContainer(containerName);
    setBrowsingContainers(false);
    setCurrentPath('');
    setIsLoading(true);
    setError('');

    try {
      const blobs = await listDirectory(containerName, ''); // Container root, so empty path
      setItems(blobs);
    } catch (err) {
      setError(`Failed to browse container: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const navigateToFolder = async (folderName) => {
    const newPath = currentPath + folderName + '/';
    setCurrentPath(newPath);
    setIsLoading(true);
    setError('');
    setSearchTerm(''); // Clear search when navigating

    try {
      const blobs = await listDirectory(currentContainer, newPath);
      setItems(blobs);
    } catch (err) {
      setError(`Failed to browse folder: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const navigateUp = async () => {
    if (currentPath) {
      // Navigate up within the container
      const pathParts = currentPath.split('/').filter(part => part);
      pathParts.pop(); // Remove the last folder
      const newPath = pathParts.length > 0 ? pathParts.join('/') + '/' : '';
      setCurrentPath(newPath);
      setIsLoading(true);
      setError('');
      setSearchTerm(''); // Clear search when navigating
      
      try {
        const blobs = await listDirectory(currentContainer, newPath);
        setItems(blobs);
      } catch (err) {
        setError(`Failed to navigate up: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    }
    // Note: No longer navigate back to container list since we're container-specific
  };

  const isVideoFile = (filename) => {
    const videoExtensions = ['.mp4', '.mov', '.avi', '.mkv', '.webm', '.m4v'];
    return videoExtensions.some(ext => filename.toLowerCase().endsWith(ext));
  };

  const downloadAndLoadFile = async (file) => {
    // Handle video files
    if (isVideoFile(file.name)) {
      if (!onLoadVideo) {
        setError('Video loading is not available');
        return;
      }

      setIsLoading(true);
      setError('');

      try {
        // Video is fetched straight from Azure rather than proxied, so the
        // player can issue range requests against it
        const videoUrl = await backend.videoUrl(file.fullPath);

        await onLoadVideo(videoUrl, file.name);
        
        // Track the loaded video file
        setLoadedVideoFile(file.fullPath);
      } catch (err) {
        setError(`Failed to load video: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Handle JSON annotation files
    if (!file.fullPath.endsWith('.json')) {
      setError('Please select a JSON annotation file or video file');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const jsonData = await backend.readJson(file.fullPath);
      onLoadAnnotation(jsonData, file.name);
      
      // Track the loaded annotation file
      setLoadedAnnotationFile(file.fullPath);
    } catch (err) {
      setError(`Failed to load annotation file: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemClick = (item) => {
    if (item.type === 'container') {
      navigateToContainer(item.name);
    } else if (item.type === 'folder') {
      navigateToFolder(item.name);
    } else {
      downloadAndLoadFile(item);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      setError('Please select a JSON file');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const fileName = file.name;
      const blobPath = currentPath ? `${currentPath}${fileName}` : fileName;

      await backend.writeBlob(blobPath, file);

      // Refresh the current directory to show the new file
      await refreshCurrentDirectory();
      
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }

    // Reset file input
    event.target.value = '';
  };

  const handleUploadCurrentAnnotations = async () => {
    if (!onGetCurrentAnnotations) return;

    setIsLoading(true);
    setError('');

    try {
      // Get current annotation data from the parent component
      const annotationData = onGetCurrentAnnotations();
      
      if (!annotationData) {
        setError('No annotation data available');
        return;
      }

      // Generate filename with timestamp and video filename (matching S key pattern)
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const videoFilename = annotationData.video_filename || '';
      const fileName = videoFilename ? `${videoFilename}_all_annotations_${timestamp}.json` : `all_annotations_${timestamp}.json`;
      const blobPath = currentPath ? `${currentPath}${fileName}` : fileName;

      const jsonString = JSON.stringify(annotationData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });

      await backend.writeBlob(blobPath, blob);

      // Refresh the current directory to show the new file
      await refreshCurrentDirectory();
      
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Set game scores, player positions and the ad rule aren't recorded in the
  // strokes file itself; the pipeline keeps them in the job request that sits
  // alongside it, as adjusted_job_request.json. Older folders may only have an
  // unadjusted job_request.json, and the adjusted copy wins where both exist.
  const loadMatchInfo = async () => {
    const jobRequests = items.filter(
      item => item.type === 'file' && /^(adjusted_)?job_request.*\.json$/i.test(item.name)
    );
    const jobRequestFile =
      jobRequests.find(item => /^adjusted_/i.test(item.name)) || jobRequests[0];

    if (!jobRequestFile) return { matchInfo: {}, jobRequestName: null };

    const jobRequest = await backend.readJson(jobRequestFile.fullPath);
    return {
      matchInfo: {
        setGameScores: jobRequest.SetGameScores,
        initialTopPlayerId: jobRequest.InitialTopPlayer,
        initialBottomPlayerId: jobRequest.InitialBottomPlayer,
        adRule: jobRequest.AD
      },
      jobRequestName: jobRequestFile.name
    };
  };

  const handleRescoreStrokes = async () => {
    if (!onGetCurrentStrokes) return;

    setIsLoading(true);
    setError('');

    try {
      const records = onGetCurrentStrokes();

      if (!records || records.length === 0) {
        setError('No strokes file is loaded. Open one before re-scoring.');
        return;
      }

      const { matchInfo, jobRequestName } = await loadMatchInfo();
      const { strokes, warnings } = await strokeScoringService.rescore(records, matchInfo);

      const baseName = (strokesFileName || '').replace(/\.[^/.]+$/, '') || 'strokes';
      const fileName = `${baseName}_rescored.json`;
      const blobPath = currentPath ? `${currentPath}${fileName}` : fileName;

      const blob = new Blob([JSON.stringify(strokes, null, 2)], { type: 'application/json' });
      await backend.writeBlob(blobPath, blob);

      await refreshCurrentDirectory();

      const notes = [...warnings];
      if (!jobRequestName) {
        notes.push('No job request found in this folder; match scores were derived from the strokes themselves');
      }

      showSuccess(`Saved ${fileName} (${strokes.length} strokes).${notes.length ? ` ${notes.join('. ')}.` : ''}`);
    } catch (err) {
      setError(`Re-scoring failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteFile = async (file) => {
    if (!confirm(`Are you sure you want to delete "${file.name}"?`)) {
      return;
    }

    setIsLoading(true);
    setError('');
    setActiveDropdown(null);

    try {
      await backend.remove(file.fullPath);

      // Refresh the current directory
      await refreshCurrentDirectory();
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadFile = async (file) => {
    setError('');

    try {
      const blob = await backend.readBlob(file.fullPath);
      const downloadUrl = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
  };

  const reset = () => {
    setStorageAccount('');
    setSasToken('');
    setCurrentContainer('tater-web');
    setCurrentPath('');
    setItems([]);
    setIsConnected(false);
    setIsLoading(false);
    setError('');
    setBrowsingContainers(true);
    setActiveDropdown(null);
    setLoadedVideoFile(null);
    setLoadedAnnotationFile(null);
    setSortBy('name');
    setSortOrder('asc');
    setSearchTerm('');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setActiveDropdown(null);
    };
    
    if (activeDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeDropdown]);

  // Ask the gateway which containers it will actually serve rather than
  // trusting the hardcoded fallback, so the two can't drift apart.
  useEffect(() => {
    if (!isOpen || !isEmployee) return;

    let cancelled = false;

    storageGatewayService.listContainers()
      .then(containers => {
        if (cancelled || !containers?.length) return;
        setContainerOptions(containers);
        setCurrentContainer(current => (
          containers.some(option => option.name === current)
            ? current
            : (containers.find(option => option.isDefault) || containers[0]).name
        ));
      })
      .catch(() => {
        // Keep the fallback list; connecting will surface any real problem
      });

    return () => { cancelled = true; };
  }, [isOpen, isEmployee]);

  if (!isOpen) return null;

  return (
    <Overlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <PopupContainer isConnected={isConnected}>
        {!isConnected && (
          <Header>
            <button
              onClick={onClose}
              style={{ 
                background: 'none',
                border: 'none',
                color: '#ff5555',
                fontSize: '24px',
                cursor: 'pointer',
                marginLeft: 'auto',
                padding: '2px',
                borderRadius: '4px',
                width: '26px',
                height: '26px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: '1'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#0d2a4a';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              ×
            </button>
          </Header>
        )}

        {status && <ErrorMessage isSuccess={status.isSuccess}>{status.message}</ErrorMessage>}


        {!isConnected && (
          <Form>
            {isEmployee ? (
              <FormRow>
                <Label>Container</Label>
                <Select
                  value={currentContainer}
                  onChange={(e) => setCurrentContainer(e.target.value)}
                >
                  {containerOptions.map(option => (
                    <option key={option.name} value={option.name}>{option.name}</option>
                  ))}
                </Select>
              </FormRow>
            ) : (
              <>
                <FormRow>
                  <Label>Storage Account Name</Label>
                  <Input
                    type="text"
                    value={storageAccount}
                    onChange={(e) => setStorageAccount(e.target.value)}
                    placeholder="mystorageaccount"
                  />
                </FormRow>

                <FormRow>
                  <Label>Container Name</Label>
                  <Input
                    type="text"
                    value={currentContainer}
                    onChange={(e) => setCurrentContainer(e.target.value)}
                    placeholder="tater-web"
                  />
                </FormRow>

                <FormRow>
                  <Label>SAS Token</Label>
                  <Input
                    type="text"
                    value={sasToken}
                    onChange={(e) => setSasToken(e.target.value)}
                    placeholder="sv=2021-06-08&sr=c&sp=rwl&se=..."
                  />
                </FormRow>
              </>
            )}
          </Form>
        )}

        <ButtonRow>
          {!isConnected ? (
            <Button 
              onClick={connectToStorage}
              disabled={isLoading || !currentContainer || (!isEmployee && (!storageAccount || !sasToken))}
            >
              {isLoading ? 'Connecting...' : 'Connect'}
            </Button>
          ) : (
            <>
              <Button 
                onClick={reset}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px'
                }}
              >
                Disconnect
              </Button>
              <Button 
                onClick={() => document.getElementById('azure-file-upload').click()}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  marginLeft: '8px'
                }}
              >
                Upload File
              </Button>
              {onGetCurrentAnnotations && (
                <Button 
                  onClick={handleUploadCurrentAnnotations}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    marginLeft: '8px'
                  }}
                >
                  Upload Current Annotations
                </Button>
              )}
              {onGetCurrentStrokes && (
                <Button
                  onClick={handleRescoreStrokes}
                  disabled={isLoading}
                  title="Re-run score prediction with the reviewed in/out calls and save the result here"
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    marginLeft: '8px'
                  }}
                >
                  Re-score & Save
                </Button>
              )}
              <input
                id="azure-file-upload"
                type="file"
                accept=".json,application/json"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
              <SearchContainer style={{ marginLeft: 'auto', marginRight: '8px' }}>
                <SearchIcon>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="m21 21-4.35-4.35"/>
                  </svg>
                </SearchIcon>
                <SearchInput
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </SearchContainer>
              <button
                onClick={onClose}
                style={{ 
                  background: 'none',
                  border: 'none',
                  color: '#ff5555',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '2px',
                  borderRadius: '4px',
                  width: '26px',
                  height: '26px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: '1'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#0d2a4a';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                ×
              </button>
            </>
          )}
        </ButtonRow>

        {isConnected && (
          <BrowserContainer>
            <PathBreadcrumb>
              <BackButton 
                onClick={navigateUp}
                disabled={!currentPath}
                title="Go back"
              >
                ←
              </BackButton>
              <PathText>
                /{currentContainer}/{currentPath}
              </PathText>
            </PathBreadcrumb>
            
            <FileList>
              {isLoading ? (
                <LoadingContainer>
                  <Spinner />
                  <LoadingText>Loading...</LoadingText>
                </LoadingContainer>
              ) : processItems(items).length === 0 && items.length > 0 ? (
                <LoadingContainer>
                  <LoadingText>
                    No files match your search
                  </LoadingText>
                </LoadingContainer>
              ) : items.length === 0 ? (
                <LoadingContainer>
                  <LoadingText>
                    No files found
                  </LoadingText>
                </LoadingContainer>
              ) : (
                <FileTable>
                  <TableHeader>
                    <ColumnHeader onClick={() => handleColumnSort('name')}>
                      Name {getSortArrow('name') && <SortArrow>{getSortArrow('name')}</SortArrow>}
                    </ColumnHeader>
                    <ColumnHeader onClick={() => handleColumnSort('lastModified')}>
                      Last Modified {getSortArrow('lastModified') && <SortArrow>{getSortArrow('lastModified')}</SortArrow>}
                    </ColumnHeader>
                    <ColumnHeader onClick={() => handleColumnSort('size')}>
                      Size {getSortArrow('size') && <SortArrow>{getSortArrow('size')}</SortArrow>}
                    </ColumnHeader>
                    <ColumnHeader disabled>Status</ColumnHeader>
                    <ColumnHeader disabled></ColumnHeader>
                  </TableHeader>
                  {processItems(items).map((item, index) => (
                    <FileItem key={index}>
                      <FileNameCell onClick={() => handleItemClick(item)}>
                        {item.type === 'container' ? <ContainerIcon /> : 
                         item.type === 'folder' ? <FolderIcon /> : <FileIcon />}
                        <FileName>{item.name}</FileName>
                      </FileNameCell>
                      <FileDataCell>
                        {item.type === 'file' && item.lastModified ? formatLastModified(item.lastModified) : ''}
                      </FileDataCell>
                      <FileDataCell>
                        {item.type === 'file' ? formatFileSize(item.size) : ''}
                      </FileDataCell>
                      <FileDataCell style={{ display: 'flex', alignItems: 'center' }}>
                        {item.type === 'file' && isVideoFile(item.name) && loadedVideoFile === item.fullPath && (
                          <LoadedIndicator>LOADED</LoadedIndicator>
                        )}
                        {item.type === 'file' && item.name.endsWith('.json') && loadedAnnotationFile === item.fullPath && (
                          <LoadedIndicator>LOADED</LoadedIndicator>
                        )}
                      </FileDataCell>
                      <FileActions>
                        {item.type === 'file' && item.name.endsWith('.json') && (
                          <>
                            <DropdownButton
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveDropdown(activeDropdown === item.fullPath ? null : item.fullPath);
                              }}
                            >
                              ⋯
                            </DropdownButton>
                            {activeDropdown === item.fullPath && (
                              <DropdownMenu onClick={(e) => e.stopPropagation()}>
                                <DropdownItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadFile(item);
                                    setActiveDropdown(null);
                                  }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                    <polyline points="7,10 12,15 17,10"/>
                                    <line x1="12" y1="15" x2="12" y2="3"/>
                                  </svg>
                                  Download
                                </DropdownItem>
                                <DropdownItem
                                  className="delete"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteFile(item);
                                  }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="3,6 5,6 21,6"/>
                                    <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
                                    <line x1="10" y1="11" x2="10" y2="17"/>
                                    <line x1="14" y1="11" x2="14" y2="17"/>
                                  </svg>
                                  Delete
                                </DropdownItem>
                              </DropdownMenu>
                            )}
                          </>
                        )}
                      </FileActions>
                    </FileItem>
                  ))}
                </FileTable>
              )}
            </FileList>
          </BrowserContainer>
        )}
      </PopupContainer>
    </Overlay>
  );
};

export default AzureStoragePopup;
